import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { TabCustomized, TabsCustomized, TabsWrapper } from '@styles/StyledTabs';
import { useTabs } from '@shared/hooks/useTabs';
import {
  getNextTabIndex,
  getSelectActiveTabIndex,
  getTabs,
} from '../../store/reducers/tabsSlice';
import { GlobalTabLabel } from './components/GlobalTabLabel';
import { HistoryButton } from './components/HistoryBtn';

export const GlobalTabs = () => {
  const navigate = useNavigate();
  const {
    closeAllTabs,
    closeLeftTabs,
    closeRightTabs,
    closeTab,
    selectTab,
    setClosedTabValue,
    setTabList,
  } = useTabs();

  const tabs = useSelector(getTabs);
  const activeTab = useSelector(getSelectActiveTabIndex);
  const handleChange = useCallback(
    (newValue: number) => {
      const route = tabs[newValue].route;

      selectTab(newValue);
      navigate(route);
    },
    [tabs]
  );

  const onCopy = (tabIndex: number) => {
    // dispatch(copyTab(tabIndex));
    navigate(tabs[tabIndex].route + '/1');
  };

  const onClose = (tabIndex: number) => {
    if (tabIndex === activeTab && tabs.length > 1) {
      const updatedTabs = [...tabs];
      updatedTabs.splice(activeTab, 1);
      const isLastTab = tabIndex === tabs.length - 1;
      const nextTabIndex = getNextTabIndex(activeTab, updatedTabs, tabIndex) + (isLastTab ? 0 : 1);
      const tab = tabs[tabIndex];
      setClosedTabValue(tab);
      navigate(tabs[nextTabIndex].route);
    }
    if (tabs.length > 1) {
      closeTab(tabIndex);
    }
  };

  const onDragEnd = useCallback((result: DropResult) => {
    if (!result?.destination) return;
    if (result?.destination.index === result.source.index) return;

    const newTabs = Array.from(tabs);
    const [removed] = newTabs.splice(result.source.index, 1);
    newTabs.splice(result.destination.index, 0, removed);

    if (result?.source.index === activeTab) {
      selectTab(result?.destination.index);
    } else if (result?.source.index < activeTab && (result?.destination.index > activeTab || result?.destination.index === activeTab)) {
      selectTab(activeTab - 1);
    } else if (result?.source.index > activeTab && (result?.destination.index < activeTab || result?.destination.index === activeTab)) {
      selectTab(activeTab + 1);
    }
    setTabList(newTabs);
  }, [tabs, activeTab, setTabList, selectTab]);

  return (
    <TabsWrapper>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="tabs" direction="horizontal">
          {props => (
            <TabsCustomized
              ref={props.innerRef}
              {...props.droppableProps}
              allowScrollButtonsMobile
              value={activeTab}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="basic tabs example"
              onChange={(_, value) => handleChange(value)}
            >
              {tabs.map(({ title, route }, index) => (
                <Draggable
                  key={route}
                  disableInteractiveElementBlocking
                  draggableId={`id-${route}`}
                  index={index}
                >
                  {props => (
                    <TabCustomized
                      ref={props.innerRef}
                      {...props.draggableProps}
                      {...props.dragHandleProps}
                      isActive={index === activeTab}
                      label={
                        <GlobalTabLabel
                          index={index}
                          title={title}
                          isActive={index === activeTab}
                          onCopy={onCopy}
                          onClose={onClose}
                          onCloseLeft={closeLeftTabs}
                          onCloseRight={closeRightTabs}
                          onCloseAll={closeAllTabs}
                        />
                      }
                      onClick={() => handleChange(index)}
                    />
                  )}
                </Draggable>
              ))}
            </TabsCustomized>
          )}
        </Droppable>
        <HistoryButton />
      </DragDropContext>
    </TabsWrapper>
  );
};